export class AuthHttpError extends Error {
  constructor(
    public override message: string,
    public authError: {
      error: string;
      type: 'NetworkError' | 'ParseError' | 'CacheError' | 'Unknown';
    }
  ) {
    super(message);
    this.name = 'AuthHttpError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface AuthHttpServiceBuilder {
  doFetch<T>(
    url: 'sign-in' | 'refresh-token',
    options?: RequestInit
  ): Promise<{
    body: T;
    headers: Record<string, string>;
    status: number;
  }>;
}

export class AuthHttpService implements AuthHttpServiceBuilder {
  constructor(
    private doFetchImpl: typeof fetch,
    private apiBaseUrl: string
  ) {}

  async doFetch<T>(url: string, options?: RequestInit) {
    try {
      const response = await this.doFetchImpl(`${this.apiBaseUrl}/${url}`, {
        ...options,
        method: options?.method || 'POST',
      });

      const body = await this.parseBody(response);

      if (!response.ok) {
        throw new AuthHttpError(`Auth HTTP error`, body);
      }

      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => (headers[key] = value));

      return { body: body as T, headers, status: response.status };
    } catch (error) {
      if (error instanceof AuthHttpError) {
        throw error;
      }

      throw new AuthHttpError(
        error instanceof Error
          ? error.message
          : String(error || 'Unknown error'),
        {
          error: 'NetworkError',
          type: 'NetworkError',
        }
      );
    }
  }

  private async parseBody(response: Response) {
    try {
      return await response.json();
    } catch (error) {
      throw new AuthHttpError(
        error instanceof Error ? error.message : String(error),
        {
          error: 'ParseError',
          type: 'ParseError',
        }
      );
    }
  }
}
